/**
 * `<ErrorBoundary>` — the per-surface `system`-error catch (frontend.md §4.2).
 *
 * frontend.md §4.2: "`system` errors — caught by the nearest `<ErrorBoundary>` (one per
 * surface, plus an app-root fallback). Shows a recoverable error panel with a 'copy error
 * report' action; the full envelope `context` is logged locally. The rest of the app
 * stays alive." So this is a real React error boundary (class component — the only place
 * the codebase needs one): a render-phase throw inside a surface is contained here, the
 * shell/nav/other surfaces keep working.
 *
 * A render-phase throw is, by definition, a frontend bug (a `system` error) — the panel
 * offers "copy error report" and a "reload surface" reset, and logs the failure locally
 * (conventions.md §4 — no secrets, structured).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { getLogger } from '@/api';
// Import directly from the source file (not the `@/hooks` barrel) so a shared component
// does not pull the entire data-hook barrel — keeps the dependency graph shallow.
import { toErrorEnvelope } from '@/hooks/useErrorHandler';
import type { ErrorEnvelope } from '@/types';

const log = getLogger('components.error-boundary');

interface ErrorBoundaryProps {
  /** A label for which surface/region this boundary wraps — used in the log + the panel. */
  boundaryName: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: ErrorEnvelope | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: toErrorEnvelope(error) };
  }

  public override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const envelope = toErrorEnvelope(error);
    log.error('surface_crashed', {
      boundary: this.props.boundaryName,
      code: envelope.code,
      // componentStack is a structure hint, not content — safe to log.
      componentStack: info.componentStack ?? undefined,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  private readonly copyReport = (): void => {
    const { error } = this.state;
    if (error && typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(JSON.stringify(error, null, 2));
    }
  };

  public override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary__title">This area hit an unexpected error</div>
        <div className="error-boundary__message">{error.message}</div>
        <div className="error-boundary__actions">
          <button type="button" onClick={this.reset}>
            Reload this area
          </button>
          <button type="button" onClick={this.copyReport}>
            Copy error report
          </button>
        </div>
        <div className="error-boundary__hint">
          The rest of Aletheia is still working — you can navigate away and back.
        </div>
      </div>
    );
  }
}
